import { Context } from 'chartjs-plugin-datalabels';

import {
  MAX_GRAPH_FONT_SIZE,
  MIN_GRAPH_DATALABEL_FONT_SIZE,
  MIN_GRAPH_FONT_SIZE,
} from '../constants/visualization-constants';

/**
 * Determines if a data label is likely to fit inside the bar
 * based on its rendered height and width in pixels.
 *
 * @param context - The Chart.js datalabel context.
 * @param minWidth - Minimum width required for label to fit horizontally.
 * @param minHeight - Minimum height required for label to fit vertically.
 * @returns true if both dimensions exceed the minimums.
 */
export function doesLabelFitInBar(
  context: Context,
  minWidth = 50,
  minHeight = 24
): boolean {
  const { height, width } = getBarDimensions(context);
  return height > minHeight && width > minWidth;
}

/**
 * Returns the height & width of a single bar.
 * Utilize ChartJS .getDatasetMeta(index) - https://www.chartjs.org/docs/latest/developers/api.html#getdatasetmeta-index
 */
export function getBarDimensions(context: Context): {
  height: number;
  width: number;
} {
  const { chart, datasetIndex, dataIndex } = context;
  if (dataIndex === undefined) return { height: 0, width: 0 };

  const meta = chart.getDatasetMeta(datasetIndex);
  const model = meta.data[dataIndex];
  const { height, width } = model.getProps(['height', 'width'], true);

  return { height, width };
}
export const getQuestionGraphFontSize = (
  isExpanded: boolean,
  isLegend = false
) => ({
  size: isExpanded
    ? isLegend
      ? MIN_GRAPH_FONT_SIZE
      : MAX_GRAPH_FONT_SIZE
    : MIN_GRAPH_FONT_SIZE, // Note: font while isExpanded should not exceed question card title
  weight: 'bold',
});

export const calculateDatalabelFont = (context: Context) => {
  const { height } = getBarDimensions(context);
  const size = Math.max(
    MIN_GRAPH_DATALABEL_FONT_SIZE, // 10px minimum font size
    Math.min(Math.floor(height * 0.8), MAX_GRAPH_FONT_SIZE) // scaled & capped at 16px
  );
  return {
    size,
    weight: 'bold' as const,
  };
};

export const calculateStackedBarDatalabelFont = (
  context: Context,
  isExpanded: boolean
) => {
  const { height, width } = getBarDimensions(context);
  const size = Math.max(
    MIN_GRAPH_DATALABEL_FONT_SIZE, // 10px minimum font size
    Math.min(Math.floor(height * (isExpanded ? 0.8 : 0.3)), MAX_GRAPH_FONT_SIZE) // scaled & capped at 16px
  );

  return {
    size: size > width ? 0 : size, // do not show if exceeds the width of the stacked bar
    weight: 'bold' as const,
  };
};

export const getChartTitleConfig = (
  chartTitle: string,
  isExpanded: boolean
) => {
  return {
    display: true,
    text: chartTitle,
    font: getQuestionGraphFontSize(isExpanded),
    position: 'top' as const,
    padding: {
      top: 10,
      bottom: 30,
    },
  };
};
